// Customers: find one, then their whole history (office-history prototype). One timeline of jobs,
// invoices, payments, calls, texts and notes; what they owe; and for owner and managers the audit
// trail (Q19). What a role may see is decided by the server; this screen only leaves out tabs.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RpcError } from '@flowology/client';
import { photoFileName, photoNote } from '@flowology/domain';
import { dateText, windowText } from '@flowology/kernel';
import { client, onNotFound } from '../api.ts';
import { Header, REVIEW_PERMS, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { useDesign } from '../blueprint/design.ts';
import { RowMore } from '../blueprint/RowMore.tsx';
import { money } from '../review/review-model.ts';
import {
  addressContactLine,
  phoneText,
  siteContactLine,
  type ContactMethod,
  type Customer,
  type Equipment,
} from '../types.ts';
import {
  byDay,
  fieldWords,
  historyRights,
  pastTense,
  SOURCE_WORDS,
  KIND_CHIP,
  KIND_LABEL,
  statementPeriod,
  jobsAndVisits,
  summaryLine,
  addressSummary,
  eventDetail,
  jobTitle,
  rowActionName,
  rowOpensName,
  lastVisit,
  timeOf,
  type AuditEntry,
  type EventKind,
  type History,
  type HistoryEvent,
  type Money,
  playable,
} from './history-model.ts';
import { Loading } from '../shared/Loading.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { MarkedUp } from '../shared/MarkedUp.tsx';
import { PhotoVisibility } from '../shared/PhotoVisibility.tsx';
import { PhotoViewer } from '../shared/PhotoViewer.tsx';
import { isOtherFile, namesOf, tileOf, type Display } from '../shared/photo-display-model.ts';
import { GalleryTools } from '../shared/GalleryTools.tsx';
import {
  ALL,
  ALL_FILES,
  filesByJob,
  galleryOf,
  type GalleryFilter,
  type VideoUpload,
} from '../shared/gallery-model.ts';
import { VideoWords } from '../shared/VideoWords.tsx';
import {
  Credits,
  InvoiceMoneyActions,
  PaymentActions,
  PaymentReceiptLine,
  PaymentStatus,
  ReceiptSend,
} from './MoneyActions.tsx';
import { useDocFocus } from '../shared/doc-link.ts';
import { openInvoice } from '../invoice/InvoiceSheet.tsx';
import { onInvoiceMoved } from '../invoice/invoice-model.ts';
import { RecordPayment, type PaymentDraft } from '../books/RecordPayment.tsx';
import {
  canResend,
  invoiceStatusWord,
  listedPayments,
  MONEY_PAGE,
  moneyPage,
  methodWord,
  moneyRights,
  showMoreText,
} from './money-model.ts';
import { SendMessageSheet } from '../messages/SendMessage.tsx';
import '../review/review.css';
import { PortalLinks } from './PortalLinks.tsx';
import { CardsOnFile } from './CardsOnFile.tsx';
import { keepsCards } from './cards-model.ts';
import './history.css';
import './bp-customers.css';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { NewCustomerForm } from '../shared/NewCustomer.tsx';
import { CallTranscript } from '../calls/CallTranscript.tsx';
import { formFromSearch, type CustomerForm } from '../shared/new-customer-model.ts';
import {
  AddContact,
  AddCustomerNote,
  AddEquipment,
  AddLocation,
  EditMethod,
  EditJobNote,
  EditTaxExempt,
  EditTerms,
  AddFile,
  AddJobNote,
  AddTag,
  ReopenJob,
  EditConsent,
  EditDetails,
  RenameContact,
  EditLocation,
} from './CustomerEdit.tsx';
import {
  consentLine,
  consentWords,
  editRights,
  equipmentLine,
  historyLine,
  noteByline,
  refusal,
  taxExemptText,
  type CustomerNote,
  type NotesHistory as NotesHistoryData,
} from './customer-edit-model.ts';
import { todayIn } from '../books/model.ts';
import { AddressRecords } from '../records/AddressRecords.tsx';
import { openJob } from '../job/job-page.ts';
import { onJobNoted } from '../job/noted.ts';
import { TaskList } from '../tasks/TaskList.tsx';
import { seesTasks } from '../tasks/tasks-model.ts';
import { openSellMembership } from '../memberships/SellMembership.tsx';
import { MEMBERSHIP_SELL_PERMS } from '../memberships/model.ts';
import {
  customerHref,
  customersFromHash,
  markCustomer,
  parseCustomerLink,
  type CustomerTab,
} from './customer-link.ts';
import { byJob, outcome, stEstimateWords, type CustomerEstimate } from './estimates-model.ts';
import { MergeCustomer } from './MergeCustomer.tsx';
import {
  ageText,
  openItems,
  visitsLeftText,
  type CustomerOpen,
  type MembershipCard,
} from './bp-customers-model.ts';
import { useCustomerSearch } from './customer-search.ts';
import { FIND_LEDE, FindCustomers } from './FindCustomers.tsx';
import { EditBillTo } from './BillTo.tsx';
import { billToLine } from './bill-to-model.ts';
import { DuplicatePairs } from './DuplicatePairs.tsx';
import { customerGone, mergedNotice, namesCustomer } from './merge-model.ts';
import { daysText, termsAt, termsText } from '../shared/terms.ts';
import { plainText } from '../shared/plain-text.ts';
import { SignaturePad } from '../shared/SignedForWork.tsx';
import { AddressOnMap } from '../shared/AddressOnMap.tsx';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';
const KINDS = Object.keys(KIND_LABEL) as EventKind[];

/** A tab's part: its own section in Riverstone; in Blueprint a part of the record's work, one zone. */
const usePart = (): 'div' | 'section' => (useDesign() === 'blueprint' ? 'div' : 'section');

export function CustomersScreen({
  nav,
  timeZone,
  open,
  onBook,
  onOpenMessages,
}: {
  nav: NavProps;
  timeZone: string;
  /**
   * A document link's customer, on their Money tab with its row in view (focus), or Blueprint's
   * Equipment's, with that piece in view (equipmentId); `at` makes each a new one.
   */
  open?: { customerId: string; focus?: string; equipmentId?: string; at: number };
  onBook: (customerId: string) => void;
  /** Inbox › Messages with this customer's texts and emails, for those who open it. */
  onOpenMessages?: (customer: { id: string; name: string }) => void;
}) {
  // A customer's own link in the address (a reload, a link someone sent): them, on its tab; `at`
  // makes one pasted in while Customers is open a new one.
  const [linked, setLinked] = useState(() => {
    const l = open ? null : parseCustomerLink(location.hash);
    return l && { ...l, at: 0 };
  });
  const [customerId, setCustomerId] = useState<string | null>(open?.customerId ?? linked?.customerId ?? null);
  const mirroring = useMirror(); // a new customer and a merge are ServiceTitan's while mirroring
  useEffect(() => {
    const onHash = () => {
      const l = customersFromHash(location.hash);
      if (!l) return;
      // Customers' own link (Back to it, or typed): the search, not the customer still open (history fix 4)
      if (l === 'search') {
        setHead(null);
        setSaid(null);
        setCustomerId(null);
        return;
      }
      setLinked({ ...l, at: Date.now() });
      setCustomerId(l.customerId);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const linkedHere = linked?.customerId === customerId ? linked : null;
  // A merge (BL-029), or an old link to a merged customer, opens the kept one with a line saying so;
  // merged meanwhile (QA-559), on the tab that was open, with the payment being typed.
  const [said, setSaid] = useState<{ customerId: string; text: string; carry?: Carry } | null>(null);
  // An open customer's name is the screen title, with their one line of facts under it.
  const [head, setHead] = useState<{ title: string; lede: string } | null>(null);
  // Opening someone (from a result, or just added) replaces what had focus: focus goes to their
  // name, once per customer, so a reload of their page later doesn't move it.
  const titled = useRef<string | null>(open?.customerId ?? null);
  // A document link's customer: focus goes to the row it named, not their name.
  useEffect(() => {
    if (!open) return;
    titled.current = open.customerId;
    setCustomerId(open.customerId);
  }, [open?.customerId, open?.at]);
  // Blueprint's title block holds the screen's first actions: the search's and the record's go there
  const bp = useDesign() === 'blueprint';
  const [headActs, setHeadActs] = useState<HTMLDivElement | null>(null);
  const back = () => {
    setHead(null);
    setSaid(null);
    setCustomerId(null);
    markCustomer(null);
  };
  useEffect(() => {
    if (!customerId) titled.current = null;
    if (!customerId || !head || titled.current === customerId) return;
    titled.current = customerId;
    const h1 = document.querySelector<HTMLElement>('h1.screen-title');
    if (h1) {
      h1.tabIndex = -1;
      h1.focus();
    }
  }, [customerId, head]);
  const onFound = (id: string) => {
    setCustomerId(id);
    markCustomer(id);
  };
  const canAdd = historyRights(nav.permissions ?? []).addCustomer && !mirroring;
  const canMerge = editRights(nav.permissions ?? [], mirroring).merge;
  // Blueprint's All customers is on ui-web's ListPage (BL-189); a customer's record keeps this screen's
  if (bp && !customerId)
    return <FindCustomers nav={nav} onOpen={onFound} canAdd={canAdd} canMerge={canMerge} />;
  return (
    <div className={bp ? 'app bp-place' : 'app'}>
      {/* Blueprint's crumb is the Header's: Customers › their name */}
      <Header
        nav={nav}
        {...(customerId ? { crumb: { back } } : {})}
        title={customerId && head ? head.title : 'Customers'}
        lede={customerId ? (head?.lede ?? '') : FIND_LEDE}
      >
        {bp ? <div className="bp-head-acts" ref={setHeadActs} /> : undefined}
      </Header>
      {customerId ? (
        <CustomerView
          key={
            open?.customerId === customerId
              ? `${customerId}-${open.at}`
              : linkedHere
                ? `${customerId}-l${linkedHere.at}`
                : customerId
          }
          id={customerId}
          {...(open?.customerId === customerId && open.focus ? { focus: open.focus } : {})}
          {...(open?.customerId === customerId && open.equipmentId ? { equipment: open.equipmentId } : {})}
          {...(linkedHere ? { startTab: linkedHere.tab } : {})}
          onTab={(t) => markCustomer(customerId, t)}
          held={nav.permissions ?? []}
          timeZone={timeZone}
          onHead={setHead}
          onBack={back}
          actsAt={headActs}
          onBook={onBook}
          {...(onOpenMessages ? { onOpenMessages } : {})}
          {...(said?.customerId === customerId ? { said: said.text } : {})}
          {...(said?.customerId === customerId && said.carry
            ? {
                startTab: said.carry.tab,
                ...(said.carry.paying ? { reopen: said.carry.paying } : {}),
                ...(said.carry.editing ? { reopenEditing: said.carry.editing } : {}),
              }
            : {})}
          onOpenCustomer={(id, text, carry) => {
            setSaid({ customerId: id, text, ...(carry ? { carry } : {}) });
            setCustomerId(id);
            markCustomer(id, carry?.tab);
          }}
        />
      ) : (
        <Search onOpen={onFound} canAdd={canAdd} canMerge={canMerge} />
      )}
    </div>
  );
}

/** Riverstone's search (Blueprint's is FindCustomers.tsx): the same search, as cards. */
function Search({
  onOpen,
  canAdd,
  canMerge,
}: {
  onOpen: (id: string) => void;
  canAdd: boolean;
  /** customers.merge: the possible duplicates' list (BL-029) */
  canMerge: boolean;
}) {
  const { query, setQuery, rows, error, searching } = useCustomerSearch();
  // Add customer (QA-323): a new company has no calls yet, so the office adds people here or in Book.
  const [adding, setAdding] = useState<CustomerForm | null>(null);
  if (adding)
    return (
      <main className="page history">
        <section className="stone card new-customer-card">
          <h2 className="h3">New customer</h2>
          <NewCustomerForm
            start={adding}
            consentNote="Consent (saved with who and when)"
            submitLabel="Add customer"
            pick="Open"
            onDone={onOpen}
            onCancel={() => setAdding(null)}
          />
        </section>
      </main>
    );
  return (
    <main className="page history">
      {canAdd && (
        <div className="row">
          <button className="btn primary" onClick={() => setAdding(formFromSearch(query))}>
            Add customer
          </button>
        </div>
      )}
      <label className="search">
        <span>Find a customer</span>
        <input
          className="field"
          type="search"
          placeholder="Name, phone, address, job or invoice number"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </label>
      {error && <p className="error">{error}</p>}
      <ul className="results">
        {rows.map((c) => (
          <li key={c.id}>
            <button className="stone qcard" onClick={() => onOpen(c.id)}>
              <b>{c.displayName}</b>
              <span className="muted small">
                Customer {c.number}
                {c.city ? ` · ${c.city}` : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {searching && (
        <p className="muted" role="status">
          Searching…
        </p>
      )}
      {!searching && !rows.length && !error && <p className="muted">No customers match.</p>}
      {canMerge && !query.trim() && <DuplicatePairs onOpen={onOpen} />}
    </main>
  );
}

type Tab = CustomerTab;
type Loc = Customer['locations'][number];
type JobRef = { id: string; title: string };
/** The edit sheet that is open over the customer, if any. */
type Editing =
  | { k: 'details' }
  | { k: 'contact' }
  | { k: 'rename'; contact: Customer['contacts'][number] }
  | { k: 'consent'; contactName: string; method: ContactMethod }
  | { k: 'editNote'; note: { id: string; rowVersion: number }; body: string; title: string }
  | { k: 'method'; contactName: string; method: ContactMethod }
  | { k: 'location'; location: Loc }
  | { k: 'billTo'; location: Loc }
  | { k: 'addLocation' }
  | { k: 'terms' }
  | { k: 'taxExempt' }
  | { k: 'equipment'; location: Loc; equipment?: Equipment }
  | { k: 'tag' }
  | { k: 'note'; job: JobRef }
  | { k: 'reopen'; job: JobRef }
  | { k: 'file'; jobs: JobRef[] }
  | { k: 'merge' };
interface TagRef {
  id: string;
  name: string;
}
/** A payment being typed on the Money tab: the invoice it was opened from, and what is typed. */
interface Paying {
  invoiceId: string;
  draft: PaymentDraft;
}
/**
 * What a customer merged while open hands the kept one: the open tab and a payment being typed
 * (QA-559), and the sheet that was open when one of its commands found the customer gone (QA-565).
 */
interface Carry {
  tab: Tab;
  paying?: Paying;
  editing?: Editing;
}

function CustomerView({
  id,
  focus,
  equipment: equipmentHere,
  startTab,
  onTab,
  held,
  timeZone,
  onHead,
  onBack,
  onBook,
  onOpenMessages,
  said: arrived,
  reopen,
  reopenEditing,
  onOpenCustomer,
  actsAt,
}: {
  id: string;
  /** An invoice or payment a document link opened: the Money tab, with its row in view. */
  focus?: string;
  /** Blueprint: a piece of equipment Customers › Equipment opened, in view on its address. */
  equipment?: string;
  /** The tab a customer's link named. */
  startTab?: Tab;
  /** A tab picked: the address follows it. */
  onTab: (t: Tab) => void;
  held: readonly string[];
  timeZone: string;
  onHead: (h: { title: string; lede: string }) => void;
  onBack: () => void;
  onBook: (customerId: string) => void;
  onOpenMessages?: (customer: { id: string; name: string }) => void;
  /** Why this customer opened: "C-1002 is merged into this customer." (BL-029) */
  said?: string;
  /** The payment that was being typed when this customer's duplicate was merged into it (QA-559). */
  reopen?: Paying;
  /** The sheet that was open on the merged customer when its command found them gone (QA-565). */
  reopenEditing?: Editing;
  /** Opens another customer, saying why: the kept one after a merge, or the one a merged link leads to. */
  onOpenCustomer: (id: string, said: string, carry?: Carry) => void;
  /** Blueprint: the title block, where the record's two first actions go */
  actsAt?: HTMLElement | null;
}) {
  const bp = useDesign() === 'blueprint';
  const rights = historyRights(held);
  // While the company mirrors ServiceTitan the customer is ServiceTitan's: booking, texting, selling
  // a membership and the record's edits are off, and the record says so once (BL-088)
  const mirroring = useMirror();
  const can = editRights(held, mirroring);
  const [customer, setCustomer] = useState<Customer | null>(null);
  // Blueprint: the piece Customers › Equipment opened, scrolled to and marked once the record is in
  useDocFocus(bp ? equipmentHere : null, !!customer);
  const [tab, setTab] = useState<Tab>(
    focus || reopen ? 'money' : startTab && (startTab !== 'audit' || rights.audit) ? startTab : 'history',
  );
  const pick = (t: Tab) => {
    setTab(t);
    onTab(t);
  };
  const [statement, setStatement] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(reopenEditing ?? null);
  // Bumped after an edit the history, photos and address summary show (a note, equipment, a file).
  const [version, setVersion] = useState(0);
  // A job note corrected or removed: the notes' history reads again
  const [notesTick, setNotesTick] = useState(0);
  // a note added to a job, or the job reopened, from its sheet (Blueprint's place for them): the
  // history reads again
  useEffect(() => onJobNoted(() => setVersion((v) => v + 1)), []);
  // A refund, credit or write-off on the Money tab: the at-a-glance stones read it again (gap #8).
  const [moneyMoved, setMoneyMoved] = useState(0);
  // Tags put on in this visit: customers.get doesn't return a customer's tags yet.
  const [tagsAdded, setTagsAdded] = useState<TagRef[]>([]);
  // "Send a text or email" (gap #3): the composer's sheet, then what went
  const [writing, setWriting] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const show = (c: Customer) => {
    setCustomer(c);
    onHead({
      title: c.displayName,
      // Blueprint's line is a sentence, never a dotted list (CONTRACT.md: copy)
      lede: bp
        ? `${c.kind === 'commercial' ? 'A business' : 'A home'}, customer ${c.number}.` +
          (c.locations[0] ? ` ${c.locations[0].line1}, ${c.locations[0].city}.` : '') +
          (c.locations.length > 1 ? ` ${c.locations.length} addresses.` : '')
        : `Customer ${c.number} · ${c.kind === 'commercial' ? 'Commercial' : 'Residential'}` +
          (c.locations[0] ? ` · ${c.locations[0].line1}, ${c.locations[0].city}` : '') +
          (c.locations.length > 1 ? ` · ${c.locations.length} addresses` : ''),
    });
  };
  // The payment being typed on the Money tab, to carry over if this customer is merged meanwhile.
  const paying = useRef<Paying | null>(null);
  // What is open now, for a follow-up that runs after this render (a command's not-found, QA-565).
  const now = useRef({ tab, editing });
  now.current = { tab, editing };
  const getCustomer = () =>
    client.call<Customer & { mergedFrom?: { id: string; number: string } }>('customers.get', { id });
  /** A merged customer's link (BL-029), or merged while open (QA-559): the kept one opens, saying where this one went. */
  const arrive = (c: Customer & { mergedFrom?: { id: string; number: string } }) => {
    const moved = mergedNotice(c);
    if (moved)
      onOpenCustomer(c.id, moved, {
        tab: now.current.tab,
        ...(paying.current ? { paying: paying.current } : {}),
        // the sheet open when its command found them gone opens again on the kept one
        ...(now.current.editing && now.current.editing.k !== 'merge' ? { editing: now.current.editing } : {}),
      });
    else show(c);
    return !!moved;
  };
  // One look per not-found answer, shared by the tab and the stones that both read money (QA-559).
  const following = useRef<Promise<boolean> | null>(null);
  /** A read for this customer answered not found: true when it was merged and the kept one opens. */
  const followMerge = () =>
    (following.current ??= getCustomer()
      .then((c) => (c.mergedFrom ? arrive(c) : false))
      .catch(() => false)
      .then((went) => {
        if (!went) following.current = null;
        return went;
      }));
  // a command for this customer answered not found (QA-565): they may have been merged meanwhile
  useEffect(() => onNotFound((_action, input) => void (namesCustomer(input, id) && followMerge())), [id]);
  useEffect(() => {
    setTagsAdded([]);
    getCustomer()
      .then(arrive)
      .catch((e: unknown) => setError(message(e)));
    // onHead is the screen's state setter, so the effect still runs once per customer.
  }, [id]);
  /** Reads the customer again (after a tag goes on or off: tags.assign answers only yes or no). */
  const reload = () =>
    getCustomer()
      .then(arrive)
      .catch((e: unknown) => setError(message(e)));
  /** "Get the latest" in a sheet after someone else's change (F-53): a refusal is the sheet's to say. */
  const readAgain = () => getCustomer().then(arrive);
  /** "Get the latest" on a job note (F-53): its words and version as they are now; gone, the sheet closes. */
  const readNoteAgain = async (noteId: string) => {
    const h = await client.call<History>('customers.history', { customerId: id, kinds: ['note'] });
    const e = h.events.find((x) => x.note?.id === noteId);
    if (!e?.note) {
      setEditing(null);
      setSent('Someone else removed that note meanwhile.');
      setVersion((v) => v + 1);
      return;
    }
    setEditing({ k: 'editNote', note: e.note, body: e.detail, title: e.title });
  };
  /** An edit that returns the customer shows it at once; the sheet closes. */
  const saved = (c: Customer) => {
    show(c);
    setEditing(null);
  };
  const refreshed = () => {
    setVersion((v) => v + 1);
    setEditing(null);
  };
  const openFile = async () => {
    const h = await client
      .call<History>('customers.history', { customerId: id, kinds: ['job'] })
      .catch(() => null);
    const jobs = [
      ...new Map((h?.events ?? []).filter((e) => e.jobId).map((e) => [e.jobId!, jobTitle(e)])),
    ].map(([jid, title]) => ({ id: jid, title }));
    if (jobs.length) setEditing({ k: 'file', jobs });
    else setError('Files go on a job, and this customer has no jobs yet.');
  };
  if (!customer)
    return (
      <main className="page history">
        <Loading error={error} what="the customer" />
      </main>
    );
  // Blueprint: what's open and the tabs are one part, the record's work (pages/ux-full's left column)
  const Main = bp ? 'section' : 'div';
  const tabs: [Tab, string][] = [
    ['history', 'History'],
    ['estimates', 'Estimates'],
    ['photos', 'Photos'],
    ['money', 'Money'],
    ...(rights.audit ? ([['audit', 'Audit']] as [Tab, string][]) : []),
  ];
  return (
    <main className="page history">
      {bp && actsAt && (rights.book || rights.message) && !mirroring
        ? createPortal(
            <>
              {rights.message && (
                <button className="btn" onClick={() => setWriting(true)}>
                  Send a text or email
                </button>
              )}
              {rights.book && (
                <button className="btn primary" onClick={() => onBook(customer.id)}>
                  Book a job
                </button>
              )}
            </>,
            actsAt,
          )
        : null}
      <div className="jobhead">
        <div className="row">
          {customer.doNotContact && <span className="chip say bad">Do not contact</span>}
          {customer.status === 'inactive' && <span className="chip say">Inactive</span>}
        </div>
        {/* in Blueprint these sit in the title block and with the parts of the record they act on */}
        {!bp && (
          <div className="row">
            {rights.book && !mirroring && (
              <button className="btn primary" onClick={() => onBook(customer.id)}>
                Book a job
              </button>
            )}
            {rights.message && !mirroring && (
              <button className="btn" onClick={() => setWriting(true)}>
                Send a text or email
              </button>
            )}
            {can.edit && (
              <button className="btn" onClick={() => setEditing({ k: 'details' })}>
                Edit details
              </button>
            )}
            {holdsAny(held, MEMBERSHIP_SELL_PERMS) && !mirroring && (
              <button className="btn" onClick={() => openSellMembership(customer.id)}>
                Offer the membership
              </button>
            )}
            {rights.statement && (
              <button className="btn" onClick={() => setStatement(true)}>
                Statement
              </button>
            )}
            {can.merge && (
              <button className="btn quiet" onClick={() => setEditing({ k: 'merge' })}>
                Merge into another…
              </button>
            )}
            <button className="btn soft" onClick={onBack}>
              ← Customers
            </button>
          </div>
        )}
      </div>
      {(sent ?? arrived) && (
        <p className="notice ok" role="status">
          {sent ?? arrived}
        </p>
      )}
      {mirroring && can.note && <p className="muted small">{MIRROR_LINE}</p>}
      <AtAGlance key={moneyMoved} customer={customer} timeZone={timeZone} onGone={followMerge} />
      <div className="cust-layout">
        <aside className="cust-side" aria-label="Contacts and locations">
          {bp ? (
            // the prototype's Reaching them: the people and the addresses as one part, each row opened
            // from its name, its other actions under its More (DESIGN.md §3: two actions a part)
            <section className="sheet side-card bp-reach" aria-labelledby="cust-reach-title">
              <div className="side-head">
                <h2 id="cust-reach-title">Reaching them</h2>
                {can.edit && (
                  <button className="btn quiet sm" onClick={() => setEditing({ k: 'contact' })}>
                    Add a contact
                  </button>
                )}
                {can.edit && (
                  <button className="btn quiet sm" onClick={() => setEditing({ k: 'addLocation' })}>
                    Add an address
                  </button>
                )}
              </div>
              <Contacts customer={customer} canEdit={can.edit} onEdit={setEditing} inZone />
              <Locations
                customer={customer}
                canEdit={can.edit}
                canPlace={can.place}
                onEdit={setEditing}
                inZone
                {...(equipmentHere ? { here: equipmentHere } : {})}
              />
            </section>
          ) : (
            <>
              <Contacts customer={customer} canEdit={can.edit} onEdit={setEditing} />
              <Locations customer={customer} canEdit={can.edit} canPlace={can.place} onEdit={setEditing} />
            </>
          )}
          <AddressRecords key={version} customer={customer} canEdit={can.note} today={todayIn(timeZone)} />
          {bp && (rights.statement || keepsCards(held)) && (
            // the prototype's Money owed: the statement and the cards on file beside the record, so the
            // work keeps its two actions (DESIGN.md §3); the invoices and payments are the Money tab's
            <section className="sheet side-card bp-money" aria-labelledby="cust-money-title">
              <div className="side-head">
                <h2 id="cust-money-title">Money owed</h2>
                {rights.statement && (
                  <button className="btn quiet sm" onClick={() => setStatement(true)}>
                    Statement
                  </button>
                )}
              </div>
              <p className="muted small">
                Their invoices and payments are on the{' '}
                <button className="bp-open" onClick={() => pick('money')}>
                  Money
                </button>{' '}
                tab.
              </p>
              {keepsCards(held) && (
                <CardsOnFile customerId={customer.id} customerName={customer.displayName} inZone />
              )}
            </section>
          )}
          {customer.billingTerms && (
            <BillingTerms customer={customer} canEdit={can.terms} onEdit={() => setEditing({ k: 'terms' })} />
          )}
          {customer.taxExempt && (
            <TaxExempt
              customer={customer}
              canEdit={can.taxExempt}
              onEdit={() => setEditing({ k: 'taxExempt' })}
            />
          )}
          <Notes
            customer={customer}
            canAdd={can.note}
            canEdit={can.edit}
            timeZone={timeZone}
            tick={notesTick}
            onEdit={() => setEditing({ k: 'details' })}
            inZone={bp}
            onHistory={() => {
              pick('history');
              // bring the tabs to the top with History focused: on a phone the timeline is below the cards
              requestAnimationFrame(() => {
                const t = document.querySelector<HTMLElement>('.cust-main [role=tab][aria-selected=true]');
                t?.closest('.tabs')?.scrollIntoView({ block: 'start' });
                t?.focus({ preventScroll: true });
              });
            }}
          />
          {/* their follow-ups (BL-004): "call Mrs Lee back about the PRV" */}
          {seesTasks(held) && (
            <section className="sheet side-card" aria-labelledby="cust-tasks-title">
              <div className="side-head">
                <h2 id="cust-tasks-title">Tasks</h2>
              </div>
              <TaskList
                key={customer.id}
                title={`${customer.displayName}’s tasks`}
                query={{ customerId: customer.id }}
                about={{ customerId: customer.id }}
                timeZone={timeZone}
                canAdd
                showAbout="job"
                empty="No open tasks for this customer."
                onOpenJob={openJob}
              />
            </section>
          )}
          {(can.edit || !!customer.tags?.length) && (
            <Tags
              customer={customer}
              tags={customer.tags ?? tagsAdded}
              canEdit={can.edit}
              onAdd={() => setEditing({ k: 'tag' })}
              onRemoved={(t) =>
                customer.tags ? void reload() : setTagsAdded(tagsAdded.filter((x) => x.id !== t.id))
              }
            />
          )}
          {bp && (customer.membership || (holdsAny(held, MEMBERSHIP_SELL_PERMS) && !mirroring)) && (
            <section className="sheet side-card" aria-labelledby="cust-plan-title">
              <div className="side-head">
                <h2 id="cust-plan-title">Membership</h2>
              </div>
              {customer.membership ? (
                <MembershipNow plan={customer.membership} />
              ) : (
                <p className="muted small">A plan with its visits included, renewed each term.</p>
              )}
              {holdsAny(held, MEMBERSHIP_SELL_PERMS) && !mirroring && (
                <div className="row side-acts">
                  <button className="btn quiet sm" onClick={() => openSellMembership(customer.id)}>
                    Offer the membership
                  </button>
                </div>
              )}
            </section>
          )}
          {bp && can.merge && (
            <section className="sheet side-card" aria-labelledby="cust-merge-title">
              <div className="side-head">
                <h2 id="cust-merge-title">The same as another?</h2>
              </div>
              <p className="muted small">
                Merge this record into the other one, so their history stays in one place.
              </p>
              <div className="row side-acts">
                <button className="btn quiet sm" onClick={() => setEditing({ k: 'merge' })}>
                  Merge into another…
                </button>
              </div>
            </section>
          )}
        </aside>
        <Main className="cust-main" {...(bp ? { 'aria-label': 'Their work' } : {})}>
          {bp && customer.open && <OpenWork open={customer.open} timeZone={timeZone} />}
          <div className="tabs" role="tablist" aria-label="Customer">
            {tabs.map(([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                className={tab === k ? 'on' : ''}
                onClick={() => pick(k)}
              >
                {label}
              </button>
            ))}
          </div>
          {error && <p className="error">{error}</p>}
          {tab === 'history' && (
            <HistoryTab
              key={version}
              customer={customer}
              timeZone={timeZone}
              onNote={can.jobNote ? (job) => setEditing({ k: 'note', job }) : undefined}
              onReopen={can.reopen ? (job) => setEditing({ k: 'reopen', job }) : undefined}
              onEditNote={(e) =>
                e.note && setEditing({ k: 'editNote', note: e.note, body: e.detail, title: e.title })
              }
              mayPlay={rights.recordings}
              {...(onOpenMessages
                ? { onMessages: () => onOpenMessages({ id: customer.id, name: customer.displayName }) }
                : {})}
            />
          )}
          {tab === 'estimates' && <EstimatesTab key={version} customerId={customer.id} timeZone={timeZone} />}
          {tab === 'photos' && (
            <PhotosTab
              key={version}
              customerId={customer.id}
              timeZone={timeZone}
              onAdd={can.files ? () => void openFile() : undefined}
              canHide={holdsAny(held, REVIEW_PERMS)}
            />
          )}
          {tab === 'money' && (
            <MoneyTab
              key={version}
              customerId={customer.id}
              customerName={customer.displayName}
              {...(focus ? { focus } : {})}
              held={held}
              timeZone={timeZone}
              onMoved={() => setMoneyMoved((n) => n + 1)}
              onGone={followMerge}
              {...(reopen ? { reopen } : {})}
              onPaying={(p) => {
                paying.current = p;
              }}
            />
          )}
          {/* in Blueprint the cards are Money owed's, beside the record */}
          {!bp && tab === 'money' && keepsCards(held) && (
            <CardsOnFile customerId={customer.id} customerName={customer.displayName} />
          )}
          {tab === 'money' && (
            <PortalLinks customerId={customer.id} canRevoke={can.revoke} timeZone={timeZone} inZone={bp} />
          )}
          {tab === 'audit' && rights.audit && <AuditTab customerId={customer.id} timeZone={timeZone} />}
        </Main>
      </div>
      {statement && (
        <StatementDialog customer={customer} timeZone={timeZone} onClose={() => setStatement(false)} />
      )}
      {editing?.k === 'merge' && (
        <MergeCustomer
          customer={customer}
          onMerged={(keptId, text) => {
            setEditing(null);
            onOpenCustomer(keptId, text);
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'details' && (
        <EditDetails
          customer={customer}
          onSaved={saved}
          onClose={() => setEditing(null)}
          onLatest={readAgain}
        />
      )}
      {editing?.k === 'rename' && (
        <RenameContact
          // the contact as last read, so "Get the latest" brings the form onto it (QA-697)
          contact={customer.contacts.find((k) => k.id === editing.contact.id) ?? editing.contact}
          only={customer.contacts.length === 1}
          onSaved={saved}
          onClose={() => setEditing(null)}
          onLatest={readAgain}
        />
      )}
      {editing?.k === 'contact' && (
        <AddContact customer={customer} onSaved={saved} onClose={() => setEditing(null)} />
      )}
      {editing?.k === 'method' && (
        <EditMethod
          contactName={editing.contactName}
          method={editing.method}
          onSaved={saved}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'editNote' && (
        <EditJobNote
          note={editing.note}
          body={editing.body}
          jobTitle={editing.title}
          onSaved={(said) => {
            setSent(said);
            setNotesTick((n) => n + 1);
            refreshed();
          }}
          onClose={() => setEditing(null)}
          onLatest={() => readNoteAgain(editing.note.id)}
        />
      )}
      {editing?.k === 'consent' && (
        <EditConsent
          customer={customer}
          contactName={editing.contactName}
          method={editing.method}
          onSaved={saved}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'location' && (
        <EditLocation
          customer={customer}
          // the address as last read, so "Get the latest" brings the form onto it
          location={customer.locations.find((l) => l.id === editing.location.id) ?? editing.location}
          withCodes={can.accessCodes}
          ownOnly={can.mirroring}
          onSaved={saved}
          onClose={() => setEditing(null)}
          onLatest={readAgain}
        />
      )}
      {editing?.k === 'billTo' && (
        <EditBillTo
          customer={customer}
          location={customer.locations.find((l) => l.id === editing.location.id) ?? editing.location}
          onSaved={(c, said) => {
            saved(c);
            setSent(said);
          }}
          onClose={() => setEditing(null)}
          onLatest={readAgain}
        />
      )}
      {editing?.k === 'taxExempt' && (
        <EditTaxExempt
          customer={customer}
          onSaved={(said) => {
            setEditing(null);
            setSent(said);
            void reload();
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'terms' && (
        <EditTerms
          customer={customer}
          onSaved={(said) => {
            setEditing(null);
            setSent(said);
            void reload();
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'addLocation' && (
        <AddLocation
          customer={customer}
          withCodes={can.accessCodes}
          onSaved={saved}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'equipment' && (
        <AddEquipment
          location={editing.location}
          {...(editing.equipment
            ? {
                equipment:
                  (customer.equipment ?? []).find((e) => e.id === editing.equipment!.id) ?? editing.equipment,
              }
            : {})}
          onLatest={readAgain}
          today={localDate(timeZone)}
          onSaved={() => {
            refreshed();
            void reload(); // customers.get lists the equipment
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'tag' && (
        <AddTag
          customer={customer}
          canCreate={can.createTag}
          onSaved={(t) => {
            // customers.get lists the customer's tags: read them again; without it, keep this visit's
            if (customer.tags) void reload();
            else if (!tagsAdded.some((x) => x.id === t.id))
              setTagsAdded([...tagsAdded, { id: t.id, name: t.name }]);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'reopen' && (
        <ReopenJob
          job={editing.job}
          onSaved={(said) => {
            setSent(said);
            refreshed();
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.k === 'note' && (
        <AddJobNote job={editing.job} onSaved={refreshed} onClose={() => setEditing(null)} />
      )}
      {editing?.k === 'file' && (
        <AddFile jobs={editing.jobs} onSaved={refreshed} onClose={() => setEditing(null)} />
      )}
      {writing && (
        <SendMessageSheet
          customerId={customer.id}
          customerName={customer.displayName}
          onSent={(what) => {
            setWriting(false);
            setSent(what);
            setVersion((v) => v + 1); // the text shows in their history
          }}
          onClose={() => setWriting(false)}
        />
      )}
    </main>
  );
}

// ───────── at a glance (office-history prototype): what a CSR on the phone needs first ─────────
// Only from the queries this screen already has: customers.money (open, billed) and customers.history
// (the last visit). The average rating and lifetime gross profit need endpoints that don't exist yet.

// dollars large, cents small and raised (Riverstone money)
function big(cents: number) {
  const [d, c] = money(cents).split('.');
  return (
    <>
      {d}
      {c !== undefined && <small>.{c}</small>}
    </>
  );
}

function AtAGlance({
  customer,
  timeZone,
  onGone,
}: {
  customer: Customer;
  timeZone: string;
  /** A read answered not found: true when the customer was merged meanwhile and the kept one opens. */
  onGone: () => Promise<boolean>;
}) {
  const [m, setM] = useState<Money | null>(null);
  const [h, setH] = useState<History | null>(null);
  const [noMoney, setNoMoney] = useState(false);
  // its open balance reads again when the invoice dialog over it moves money (QA-805)
  const [moved, setMoved] = useState(0);
  useEffect(() => onInvoiceMoved(window, customer.id, () => setMoved((n) => n + 1)), [customer.id]);
  useEffect(() => {
    client
      .call<Money>('customers.money', { customerId: customer.id })
      .then(setM)
      .catch(async (e: unknown) => {
        if (customerGone(e) && (await onGone())) return;
        setNoMoney(true); // a role that may not see money: the stones leave it out
      });
    client
      .call<History>('customers.history', { customerId: customer.id, kinds: ['job'] })
      .then(setH)
      .catch((e: unknown) => void (customerGone(e) && onGone()));
  }, [customer.id, moved]);
  const billed = m ? m.invoices.filter((i) => i.status !== 'void').reduce((t, i) => t + i.totalCents, 0) : 0;
  const last = h ? lastVisit(h.events) : null;
  const day = new Intl.DateTimeFormat('en-CA', { timeZone, month: 'short', day: 'numeric', year: 'numeric' });
  const blank = <span className="ph-pebble" aria-hidden="true" />;
  // Blueprint's strip also says what's open and their plan (customers.get), as the prototype's facts
  const bp = useDesign() === 'blueprint';
  // in Blueprint the figures are one strip, not a card each (DESIGN.md §3a)
  const tile = bp ? 'tile' : 'sheet tile';
  const open = customer.open;
  const plan = customer.membership;
  return (
    <div className="tiles glance" aria-label="At a glance">
      {bp && open && (
        <div className={tile}>
          <b>{open.jobs.count + open.estimates.count}</b>
          <span className="tile-label">Open</span>
          <span className="muted small">jobs and estimates</span>
        </div>
      )}
      {!noMoney && (
        <div className={tile}>
          {m ? <b className={m.overdueCents > 0 ? 'bad' : ''}>{big(m.openCents)}</b> : blank}
          <span className="tile-label">Open balance</span>
          {m && m.overdueCents > 0 && <span className="small bad-word">{money(m.overdueCents)} overdue</span>}
        </div>
      )}
      {!noMoney && (
        <div className={tile}>
          {m ? <b>{big(billed)}</b> : blank}
          {/* with tax: ServiceTitan's lifetime revenue is before it (history fix 1) */}
          <span className="tile-label">Billed to date, incl. tax</span>
          {m && <span className="muted small">{money(m.paidCents)} paid</span>}
        </div>
      )}
      <div className={tile}>
        {h ? <b>{last ? day.format(new Date(last.at)) : 'None yet'}</b> : blank}
        <span className="tile-label">Last visit</span>
        {h && (
          <span className="muted small">
            {jobsAndVisits(h.summary)}
            {last ? ` · ${jobTitle(last)}` : ''}
          </span>
        )}
      </div>
      {bp && plan !== undefined && (
        <div className={tile}>
          <b>{plan ? plan.name : 'None'}</b>
          <span className="tile-label">Membership</span>
          <span className="muted small">{plan ? `Renews ${longDate(plan.endsOn)}` : 'Not a member'}</span>
        </div>
      )}
    </div>
  );
}

/** Blueprint's What's open (pages/ux-full OpenWork): jobs and estimates on hairlines, five then all. */
function OpenWork({ open, timeZone }: { open: CustomerOpen; timeZone: string }) {
  const [all, setAll] = useState(false);
  const when = (start: string, end: string) =>
    `${dateText(start, timeZone, { weekday: 'short', year: false, now: new Date() })}, ${windowText(start, end, timeZone)}`;
  const items = openItems(open, when);
  const shown = all ? items : items.slice(0, 5);
  const total = open.jobs.count + open.estimates.count;
  return (
    <div className="bp-z bp-open-work">
      <h2 id="cust-open-title">What’s open</h2>
      {items.length ? (
        <ul className="bp-items">
          {shown.map((i) => (
            <li key={i.key}>
              <span className="bp-kind">{i.kind}</span>
              <span>
                <button
                  className="bp-open"
                  aria-label={`Open ${i.kind.toLowerCase()} ${i.ref}`}
                  onClick={() => openJob(i.jobId)}
                >
                  {i.ref}
                </button>
                <span className="bp-meta">{i.text}</span>
                {i.techs.length > 0 && <span className="bp-meta">{i.techs.join(', ')}</span>}
              </span>
              <span className={`bp-state bp-state-${i.tone}`}>{i.state}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">Nothing open: every job is finished.</p>
      )}
      {total > shown.length && !all && (
        <button className="btn quiet sm bp-more" onClick={() => setAll(true)}>
          Show all {total}
        </button>
      )}
    </div>
  );
}

/** The plan they're on: its visits drawn as marks, its renewal, whether it renews by itself. */
function MembershipNow({ plan }: { plan: MembershipCard }) {
  const marks = Array.from({ length: Math.min(plan.visitsTotal, 6) }, (_, i) => i < plan.visitsUsed);
  return (
    <div className="bp-plan">
      <b>{plan.name}</b>
      {plan.visitsTotal > 0 && (
        <p className="bp-visits">
          <span aria-hidden="true">
            {marks.map((used, i) => (
              <i key={i} className={used ? 'used' : undefined} />
            ))}
          </span>
          {visitsLeftText(plan)}
        </p>
      )}
      <dl className="bp-kv">
        <dt>Renews</dt>
        <dd>{longDate(plan.endsOn)}</dd>
        <dt>Renewal</dt>
        <dd>{plan.autoRenew ? 'By itself' : <span className="bp-wait">Auto-renew is off</span>}</dd>
      </dl>
    </div>
  );
}

function consentWord(m: ContactMethod, dnc: boolean) {
  const words = consentWords(m, dnc);
  const tone = dnc || m.doNotContact ? 'bad' : m.consent ? 'ok' : 'warn';
  return <span className={`chip say ${tone}`}>{words}</span>;
}

function Contacts({
  customer,
  canEdit,
  onEdit,
  inZone = false,
}: {
  customer: Customer;
  canEdit: boolean;
  onEdit: (e: Editing) => void;
  /** Blueprint: a part of Reaching them; each person opens from their name, the rest under More */
  inZone?: boolean;
}) {
  const label = (m: ContactMethod) => (m.kind === 'phone' ? phoneText(m.value) : m.value);
  if (inZone)
    return (
      <div className="bp-part" aria-label="Contacts">
        <h3>People</h3>
        {customer.contacts.length ? (
          <ul className="side-list">
            {customer.contacts.map((c) => (
              <li key={c.id}>
                <span className="contact-head">
                  {canEdit ? (
                    <button
                      className="bp-open"
                      aria-label={`Edit ${c.name}`}
                      onClick={() => onEdit({ k: 'rename', contact: c })}
                    >
                      {c.name}
                    </button>
                  ) : (
                    <b>{c.name}</b>
                  )}
                  {c.title && <span className="muted small">{c.title}</span>}
                </span>
                {c.methods.map((m) => (
                  <div key={m.id} className="method small">
                    <span>
                      {label(m)}
                      {m.isPrimary ? ', tried first' : ''}
                    </span>{' '}
                    {consentWord(m, customer.doNotContact)}
                    {consentLine(m, shortDay) && (
                      <span className="consent-was">{consentLine(m, shortDay)}</span>
                    )}
                  </div>
                ))}
                {canEdit && c.methods.length > 0 && (
                  <RowMore about={c.name}>
                    {c.methods.map((m) => (
                      <div key={m.id} className="bp-more-row">
                        <span className="small">{label(m)}</span>
                        <button
                          className="btn quiet sm"
                          aria-label={`Consent for ${label(m)}`}
                          onClick={() => onEdit({ k: 'consent', contactName: c.name, method: m })}
                        >
                          Consent
                        </button>
                        <button
                          className="btn quiet sm"
                          aria-label={`Change ${label(m)}`}
                          onClick={() => onEdit({ k: 'method', contactName: c.name, method: m })}
                        >
                          Change
                        </button>
                      </div>
                    ))}
                  </RowMore>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">No contacts yet.</p>
        )}
      </div>
    );
  return (
    <section className="sheet side-card" aria-label="Contacts">
      <div className="side-head">
        <h2>Contacts</h2>
        {canEdit && (
          <button className="btn quiet sm" onClick={() => onEdit({ k: 'contact' })}>
            Add a contact
          </button>
        )}
      </div>
      {customer.contacts.length ? (
        <ul className="side-list">
          {customer.contacts.map((c) => (
            <li key={c.id}>
              <span className="contact-head">
                <b>{c.name}</b>
                {c.title && <span className="muted small"> · {c.title}</span>}
                {canEdit && (
                  <button
                    className="btn quiet sm"
                    aria-label={`Edit ${c.name}`}
                    onClick={() => onEdit({ k: 'rename', contact: c })}
                  >
                    Edit
                  </button>
                )}
              </span>
              {c.methods.map((m) => (
                <div key={m.id} className="method small">
                  <span>
                    {m.kind === 'phone' ? phoneText(m.value) : m.value}
                    {m.isPrimary ? ' · primary' : ''}
                  </span>{' '}
                  {consentWord(m, customer.doNotContact)}
                  {canEdit && (
                    <button
                      className="btn quiet sm"
                      aria-label={`Consent for ${m.kind === 'phone' ? phoneText(m.value) : m.value}`}
                      onClick={() => onEdit({ k: 'consent', contactName: c.name, method: m })}
                    >
                      Consent
                    </button>
                  )}
                  {canEdit && (
                    // correct it, make it primary or remove it (the CRM walk)
                    <button
                      className="btn quiet sm"
                      aria-label={`Change ${m.kind === 'phone' ? phoneText(m.value) : m.value}`}
                      onClick={() => onEdit({ k: 'method', contactName: c.name, method: m })}
                    >
                      Change
                    </button>
                  )}
                  {consentLine(m, shortDay) && (
                    <span className="consent-was">{consentLine(m, shortDay)}</span>
                  )}
                </div>
              ))}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">No contacts yet.</p>
      )}
    </section>
  );
}

// Their billing terms (F-29): days to pay and the PO rule, for their own work; an address billed to
// someone else (a property manager) follows that bill-to's, edited on their record.
function BillingTerms({
  customer,
  canEdit,
  onEdit,
}: {
  customer: Customer;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const t = customer.billingTerms!;
  const billed = customer.locations.filter((l) => l.billTo);
  return (
    <section className="sheet side-card" aria-label="Billing terms">
      <div className="side-head">
        <h2>Billing terms</h2>
        {canEdit && (
          <button className="btn quiet sm" aria-label="Edit the billing terms" onClick={onEdit}>
            Edit
          </button>
        )}
      </div>
      <p className="small">
        {daysText(t.termsDays)} · {t.poRequired ? 'PO required on every job' : 'no PO needed'}
      </p>
      {billed.map((l) => (
        <p key={l.id} className="small muted">
          Invoices for {l.name ?? l.line1} go to{' '}
          <a href={customerHref(l.billTo!.customerId)}>{l.billTo!.name}</a>
          {termsText(termsAt(customer, l.id).terms) ? `: ${termsText(termsAt(customer, l.id).terms)}` : ''}.
        </p>
      ))}
    </section>
  );
}

/** Whether they're charged tax (BL-094): the words their new invoices and estimates carry. */
function TaxExempt({
  customer,
  canEdit,
  onEdit,
}: {
  customer: Customer;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <section className="sheet side-card" aria-label="Tax">
      <div className="side-head">
        <h2>Tax</h2>
        {canEdit && (
          <button className="btn quiet sm" aria-label="Edit the tax exemption" onClick={onEdit}>
            Edit
          </button>
        )}
      </div>
      <p className="small">{taxExemptText(customer.taxExempt!)}</p>
    </section>
  );
}

/** An address's equipment on record (customers.get): in service with Edit, retired behind a disclosure. */
function EquipmentAt({
  equipment,
  at,
  onEdit,
  here,
}: {
  equipment: Equipment[];
  at: string;
  onEdit?: (e: Equipment) => void;
  /** Blueprint: the piece Customers › Equipment opened; each piece is marked so it can be found. */
  here?: string;
}) {
  const bp = useDesign() === 'blueprint';
  const live = equipment.filter((e) => !e.retiredAt);
  const retired = equipment.filter((e) => e.retiredAt);
  if (!equipment.length) return null;
  return (
    <div className="equip">
      {live.length > 0 && (
        <ul className="equip-list" aria-label={`Equipment at ${at}`}>
          {live.map((e) => (
            <li key={e.id} {...(bp ? { 'data-doc': e.id } : {})}>
              <span className="small">
                <b>{e.kind}</b>
                {equipmentLine(e) && <span className="muted"> · {equipmentLine(e)}</span>}
                {/* Blueprint: how old it is (customers.get's ageYears) */}
                {bp && ageText(e.ageYears) && <span className="bp-meta">{ageText(e.ageYears)}</span>}
              </span>
              {onEdit && (
                <button
                  className="btn quiet sm"
                  aria-label={`Edit the ${e.kind.toLowerCase()} at ${at}`}
                  onClick={() => onEdit(e)}
                >
                  Edit
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {retired.length > 0 && (
        <details
          className="equip-retired"
          {...(bp && retired.some((e) => e.id === here) ? { open: true } : {})}
        >
          <summary className="small">Retired ({retired.length})</summary>
          <ul className="equip-list">
            {retired.map((e) => (
              <li key={e.id} className="muted small" {...(bp ? { 'data-doc': e.id } : {})}>
                {e.kind}
                {e.retiredReason ? `: ${e.retiredReason}` : ''}
                {e.retiredAt ? ` · ${longDate(e.retiredAt.slice(0, 10))}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Up to this many addresses, each shows its map; more, each its source line and Navigate only. */
const MAPPED_ADDRESSES = 3;
/** An address on one line, as Navigate and the map's name take it. */
const addressLine = (l: Customer['locations'][number]) =>
  `${[l.line1, l.line2, l.city].filter(Boolean).join(', ')}${l.postal ? ` ${l.postal}` : ''}`;

function Locations({
  customer,
  canEdit,
  canPlace,
  onEdit,
  inZone = false,
  here,
}: {
  customer: Customer;
  canEdit: boolean;
  /** An address's access and site contact (its own columns): editable while mirroring too. */
  canPlace: boolean;
  onEdit: (e: Editing) => void;
  /** Blueprint: a part of Reaching them; each address opens from its name, the rest under More */
  inZone?: boolean;
  /** Blueprint: the piece of equipment Customers › Equipment opened. */
  here?: string;
}) {
  if (inZone)
    return (
      <div className="bp-part" aria-label="Locations">
        <h3>{customer.locations.length === 1 ? 'Address' : `Addresses (${customer.locations.length})`}</h3>
        {customer.locations.length === 0 && <p className="muted small">No address yet.</p>}
        <ul className="side-list">
          {customer.locations.map((l) => {
            const at = l.name ?? l.line1;
            const equipment = (customer.equipment ?? []).filter((e) => e.locationId === l.id);
            return (
              <li key={l.id}>
                {canEdit || canPlace ? (
                  <button
                    className="bp-open"
                    aria-label={`Edit ${at}`}
                    onClick={() => onEdit({ k: 'location', location: l })}
                  >
                    {at}
                  </button>
                ) : (
                  <b>{at}</b>
                )}
                <span className="muted small">
                  {l.name ? `${l.line1}, ` : ''}
                  {l.line2 ? `${l.line2}, ` : ''}
                  {l.city}
                  {l.postal ? ` ${l.postal}` : ''}
                </span>
                {/* its point on the map, where it came from, and Navigate in Google Maps (BL-018, the
                    maps design's places 2 and 8); a long list keeps to words, each map being a load */}
                <div className="flw cust-where">
                  <AddressOnMap
                    address={addressLine(l)}
                    point={l.point}
                    showAddress={false}
                    withMap={customer.locations.length <= MAPPED_ADDRESSES}
                  />
                </div>
                {l.accessNotes && <span className="small access">{l.accessNotes}</span>}
                {l.accessCodes && <span className="small access">Codes: {l.accessCodes}</span>}
                {(l.billTo || canEdit) && (
                  <span className={`small bill-to-line${l.billTo ? '' : ' muted'}`}>
                    {billToLine(l, customer.displayName)}
                  </span>
                )}
                {l.siteContact && <span className="small">{siteContactLine(l.siteContact)}</span>}
                {(l.contacts ?? [])
                  .filter((k) => k.id !== l.siteContact?.id)
                  .map((k) => (
                    <span key={k.id} className="small">
                      {addressContactLine(k)}
                    </span>
                  ))}
                <EquipmentAt equipment={equipment} at={at} {...(here ? { here } : {})} />
                {canEdit && (
                  <RowMore about={at}>
                    <div className="bp-more-row">
                      <button
                        className="btn quiet sm"
                        aria-label={`Change who ${at} bills to`}
                        onClick={() => onEdit({ k: 'billTo', location: l })}
                      >
                        Change who it bills to
                      </button>
                    </div>
                    <div className="bp-more-row">
                      <button
                        className="btn quiet sm"
                        aria-label={`Add equipment at ${at}`}
                        onClick={() => onEdit({ k: 'equipment', location: l })}
                      >
                        Add equipment
                      </button>
                    </div>
                    {equipment
                      .filter((e) => !e.retiredAt)
                      .map((e) => (
                        <div key={e.id} className="bp-more-row">
                          <button
                            className="btn quiet sm"
                            aria-label={`Edit the ${e.kind.toLowerCase()} at ${at}`}
                            onClick={() => onEdit({ k: 'equipment', location: l, equipment: e })}
                          >
                            Edit the {e.kind.toLowerCase()}
                          </button>
                        </div>
                      ))}
                  </RowMore>
                )}
              </li>
            );
          })}
        </ul>
        {customer.billingAddress && (
          <p className="small billing">
            <span className="muted">Invoices go to </span>
            {customer.billingAddress.line1}
            {customer.billingAddress.line2 ? `, ${customer.billingAddress.line2}` : ''},{' '}
            {customer.billingAddress.city}
            {customer.billingAddress.postal ? ` ${customer.billingAddress.postal}` : ''}
          </p>
        )}
      </div>
    );
  return (
    <section className="sheet side-card" aria-label="Locations">
      <div className="side-head">
        <h2>{customer.locations.length === 1 ? 'Location' : `Locations (${customer.locations.length})`}</h2>
        {canEdit && (
          <button className="btn quiet sm" onClick={() => onEdit({ k: 'addLocation' })}>
            Add an address
          </button>
        )}
      </div>
      <ul className="side-list">
        {customer.locations.map((l) => (
          <li key={l.id}>
            <b>{l.name ?? l.line1}</b>
            <span className="muted small">
              {l.name ? `${l.line1}, ` : ''}
              {l.line2 ? `${l.line2}, ` : ''}
              {l.city}
              {l.postal ? ` ${l.postal}` : ''}
            </span>
            {l.accessNotes && <span className="small access">{l.accessNotes}</span>}
            {l.accessCodes && <span className="small access">Codes: {l.accessCodes}</span>}
            {/* who its invoices go to (customers.setBillTo): changed here, by whoever edits customers */}
            {(l.billTo || canEdit) && (
              <span className={`small bill-to-line${l.billTo ? '' : ' muted'}`}>
                {billToLine(l, customer.displayName)}
                {canEdit && (
                  <button
                    className="btn quiet sm"
                    aria-label={`Change who ${l.name ?? l.line1} bills to`}
                    onClick={() => onEdit({ k: 'billTo', location: l })}
                  >
                    Change
                  </button>
                )}
              </span>
            )}
            {l.siteContact && <span className="small">{siteContactLine(l.siteContact)}</span>}
            {/* the people only on this address, ServiceTitan's location contacts (history fix 4) */}
            {(l.contacts ?? [])
              .filter((k) => k.id !== l.siteContact?.id)
              .map((k) => (
                <span key={k.id} className="small">
                  {addressContactLine(k)}
                </span>
              ))}
            <EquipmentAt
              equipment={(customer.equipment ?? []).filter((e) => e.locationId === l.id)}
              at={l.name ?? l.line1}
              {...(canEdit
                ? { onEdit: (e: Equipment) => onEdit({ k: 'equipment', location: l, equipment: e }) }
                : {})}
            />
            {(canEdit || canPlace) && (
              <div className="row side-acts">
                <button
                  className="btn quiet sm"
                  aria-label={`Edit ${l.name ?? l.line1}`}
                  onClick={() => onEdit({ k: 'location', location: l })}
                >
                  {canEdit ? 'Edit address' : 'Access and site contact'}
                </button>
                {canEdit && (
                  <button
                    className="btn quiet sm"
                    aria-label={`Add equipment at ${l.name ?? l.line1}`}
                    onClick={() => onEdit({ k: 'equipment', location: l })}
                  >
                    Add equipment
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {customer.billingAddress && (
        <p className="small billing">
          <span className="muted">Invoices go to </span>
          {customer.billingAddress.line1}
          {customer.billingAddress.line2 ? `, ${customer.billingAddress.line2}` : ''},{' '}
          {customer.billingAddress.city}
          {customer.billingAddress.postal ? ` ${customer.billingAddress.postal}` : ''}
        </p>
      )}
    </section>
  );
}

// Notes on the customer, read by everyone who opens them: the office notes at the top
// (customers.update's notes, edited in place), then the dated notes about them, newest first
// (customers.notes, added with customers.addNote and never edited). Notes on a job are in History.
/** A note's text with its line breaks, held to 4 lines with More and Less when it runs longer. */
function NoteBody({ body }: { body: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [long, setLong] = useState(false);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return;
    const measure = () => setLong(el.scrollHeight > el.clientHeight + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [body, open]);
  return (
    <>
      <p ref={ref} className={`small cust-notes${open ? '' : ' clamp'}`}>
        {body}
      </p>
      {(long || open) && (
        <button className="btn quiet sm more" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? 'Less' : 'More'}
        </button>
      )}
    </>
  );
}

const NOTES_SHOWN = 3;
/**
 * What the notes said before (customers.notesHistory): the office notes' earlier versions, kept from
 * the day that began, and the corrections and removals of notes on their jobs. Read when opened, and
 * again after a change.
 */
function NotesHistory({ customer, timeZone, tick }: { customer: Customer; timeZone: string; tick: number }) {
  const [open, setOpen] = useState(false);
  const [h, setH] = useState<NotesHistoryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    client
      .call<NotesHistoryData>('customers.notesHistory', { customerId: customer.id })
      .then((r) => {
        setH(r);
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  }, [open, tick, customer.id, customer.notes]);
  // the byline's time half: "2:41 p.m.", "Yesterday, 4:10 p.m.", "Oct 14"
  const when = (iso: string) =>
    noteByline({ authorName: '', occurredAt: iso }, timeZone).split(' · ').slice(1).join(' · ');
  const rows = h
    ? [
        ...h.office.map((v) => ({ at: v.at, line: historyLine({ kind: 'office', ...v }), was: v.before })),
        ...h.jobNotes.map((n) => ({ at: n.at, line: historyLine({ kind: 'job', ...n }), was: n.before })),
      ].sort((a, b) => b.at.localeCompare(a.at))
    : [];
  return (
    <details className="notes-history" onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary className="small">History of these notes</summary>
      {error ? (
        <p className="error small">{error}</p>
      ) : !h ? (
        <Loading error={null} what="the notes’ history" />
      ) : (
        <>
          {rows.length ? (
            <ul className="note-list">
              {rows.map((r, i) => (
                <li key={`${r.at}-${i}`}>
                  <span className="small">
                    {r.line} · {when(r.at)}
                  </span>
                  {r.was !== null && (
                    <p className="small muted was">
                      Was: <q>{r.was}</q>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted small">No changes yet.</p>
          )}
          <p className="muted small">
            Earlier versions of the office notes are kept from {longDate(h.since)}.
          </p>
        </>
      )}
    </details>
  );
}

function Notes({
  customer,
  canAdd,
  canEdit,
  timeZone,
  tick,
  onEdit,
  onHistory,
  inZone = false,
}: {
  customer: Customer;
  /** Add a note (customers.addNote): Flowology's own, while mirroring too. */
  canAdd: boolean;
  /** The office notes on the customer (customers.update). */
  canEdit: boolean;
  timeZone: string;
  /** Bumped when a job note changes, for the notes' history. */
  tick: number;
  onEdit: () => void;
  /** Open the History tab, where the notes on jobs are. */
  onHistory: () => void;
  /** Blueprint: Edit details here (it holds the office notes), Show all as the list's last row, and
   * History named in a sentence, so the part keeps two actions (DESIGN.md §3) */
  inZone?: boolean;
}) {
  const [notes, setNotes] = useState<CustomerNote[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [all, setAll] = useState(false);
  // "Note added." for a screen reader (and a glance), in a polite region; focus stays where it is
  const [said, setSaid] = useState('');
  // the sheet unmounts when it's done: focus goes back to Add a note, not to the page
  const addRef = useRef<HTMLButtonElement>(null);
  const closeSheet = () => {
    setAdding(false);
    requestAnimationFrame(() => addRef.current?.focus());
  };
  useEffect(() => {
    if (!said) return;
    const t = setTimeout(() => setSaid(''), 4000);
    return () => clearTimeout(t);
  }, [said]);
  const load = () =>
    client
      .call<{ notes: CustomerNote[] }>('customers.notes', { customerId: customer.id })
      .then((r) => {
        setNotes(r.notes.filter((n) => n.kind === 'customer'));
        setError(null);
      })
      .catch((e: unknown) => setError(refusal(e)));
  useEffect(() => {
    void load();
    // read again only for another customer
  }, [customer.id]);
  const shown = notes && (all ? notes : notes.slice(0, NOTES_SHOWN));
  return (
    <section className="sheet side-card" aria-label="Notes">
      <div className="side-head">
        <h2>Notes</h2>
        {canAdd && (
          <button ref={addRef} className="btn quiet sm" onClick={() => setAdding(true)}>
            Add a note
          </button>
        )}
        {inZone && canEdit && (
          <button className="btn quiet sm" onClick={onEdit}>
            Edit details
          </button>
        )}
      </div>
      <p role="status" className={said ? 'small note-said' : 'note-said-empty'}>
        {said}
      </p>
      {customer.notes ? (
        <div className="office-notes">
          <div className="office-text">
            <NoteBody body={customer.notes} />
          </div>
          {canEdit && !inZone && (
            <button className="btn quiet sm" aria-label="Edit the office notes" onClick={onEdit}>
              Edit
            </button>
          )}
        </div>
      ) : (
        customer.notes !== undefined &&
        canEdit &&
        !inZone && (
          <button className="btn quiet sm add-office" onClick={onEdit}>
            Add office notes
          </button>
        )
      )}
      {error ? (
        <p className="error small" role="alert">
          {error}{' '}
          <button className="btn quiet sm" onClick={() => void load()}>
            Try again
          </button>
        </p>
      ) : !shown ? (
        <Loading error={null} what="the notes" retry={() => void load()} />
      ) : shown.length === 0 ? (
        <p className="muted small">No notes yet.</p>
      ) : (
        <ul className="note-list">
          {shown.map((n) => (
            <li key={n.id}>
              <NoteBody body={n.body} />
              <span className="muted small">{noteByline(n, timeZone)}</span>
            </li>
          ))}
          {inZone && notes && notes.length > NOTES_SHOWN && (
            <li>
              <button className="btn quiet sm" aria-expanded={all} onClick={() => setAll(!all)}>
                {all ? 'Show fewer' : `Show all (${notes.length})`}
              </button>
            </li>
          )}
        </ul>
      )}
      {!inZone && notes && notes.length > NOTES_SHOWN && (
        <button className="btn quiet sm" aria-expanded={all} onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all (${notes.length})`}
        </button>
      )}
      <NotesHistory customer={customer} timeZone={timeZone} tick={tick} />
      {inZone ? (
        <p className="muted small">
          Job notes are in{' '}
          <button className="bp-open" onClick={onHistory}>
            History
          </button>
          .
        </p>
      ) : (
        <button className="btn quiet sm to-history" onClick={onHistory}>
          Job notes are in History
        </button>
      )}
      {adding && (
        <AddCustomerNote
          customer={customer}
          onSaved={() => {
            closeSheet();
            setSaid('Note added.');
            void load();
          }}
          onClose={closeSheet}
        />
      )}
    </section>
  );
}

// The customer's tags (customers.get's), or, from an older server, the ones put on in this visit.
function Tags({
  customer,
  tags: added,
  canEdit,
  onAdd,
  onRemoved,
}: {
  customer: Customer;
  tags: TagRef[];
  canEdit: boolean;
  onAdd: () => void;
  onRemoved: (t: TagRef) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const remove = async (t: TagRef) => {
    setBusy(t.id);
    setError(null);
    try {
      await client.call('tags.unassign', { tagId: t.id, entityType: 'customer', entityId: customer.id });
      onRemoved(t);
    } catch (e) {
      setError(refusal(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="sheet side-card" aria-label="Tags">
      <div className="side-head">
        <h2>Tags</h2>
        {canEdit && (
          <button className="btn quiet sm" onClick={onAdd}>
            Add a tag
          </button>
        )}
      </div>
      {added.length > 0 && (
        <ul className="tag-list">
          {added.map((t) => (
            <li key={t.id}>
              <span className="chip">{t.name}</span>
              {canEdit && (
                <button
                  className="btn quiet sm"
                  aria-label={`Remove the tag ${t.name}`}
                  disabled={busy === t.id}
                  onClick={() => void remove(t)}
                >
                  {busy === t.id ? 'Removing…' : 'Remove'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && !added.length && customer.tags && <p className="muted small">No tags yet.</p>}
      {error && <p className="error small">{error}</p>}
    </section>
  );
}

const shortDay = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));

const localDate = (timeZone: string, at = new Date()) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    at,
  );
const longDate = (d: string) =>
  new Intl.DateTimeFormat('en-CA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${d}T12:00:00Z`));

function HistoryTab({
  customer,
  timeZone,
  onNote,
  onReopen,
  mayPlay = false,
  onMessages,
  onEditNote,
}: {
  customer: Customer;
  timeZone: string;
  /** A text or email row opens the customer's conversation in Messages. */
  onMessages?: () => void;
  /** A job note this person may correct or remove (its writer, or notes.manage). */
  onEditNote?: (e: HistoryEvent) => void;
  /** Add a note to a job (notes.add), for those who may. */
  onNote?: (job: JobRef) => void;
  /** Reopen a completed job for a warranty callback (jobs.reopen), for the owner and managers (F-31). */
  onReopen?: (job: JobRef) => void;
  /** Play a ServiceTitan call's recording (migration.playRecording): owner and managers. */
  mayPlay?: boolean;
}) {
  const Part = usePart();
  const bp = useDesign() === 'blueprint';
  const [kinds, setKinds] = useState<EventKind[]>([]);
  const [locationId, setLocationId] = useState('');
  const [history, setHistory] = useState<History | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<History>('customers.history', {
        customerId: customer.id,
        ...(kinds.length ? { kinds } : {}),
        ...(locationId ? { locationId } : {}),
      })
      .then((h) => {
        setHistory(h);
        setError(null);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [customer.id, kinds, locationId]);
  const toggle = (k: EventKind) => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]);
  /** What a row opens, in Blueprint from its title: its job, its invoice, or the conversation. */
  const opensFrom = (e: HistoryEvent): { label: string; go: () => void } | null => {
    const label = rowOpensName(e, customer.displayName);
    if (!label) return null;
    if (e.kind === 'job') return { label, go: () => openJob(e.jobId!) };
    if (e.kind === 'invoice') return { label, go: () => openInvoice(e.invoiceId!) };
    return onMessages ? { label, go: onMessages } : null;
  };
  return (
    <>
      <AddressSummaryCard customer={customer} locationId={locationId} timeZone={timeZone} />
      <Part className={Part === 'div' ? 'bp-tab-part' : 'card'} aria-label="History">
        <div className="filters" role="group" aria-label="Show">
          <button
            className={`btn quiet${kinds.length ? '' : ' on'}`}
            aria-pressed={!kinds.length}
            onClick={() => setKinds([])}
          >
            All
          </button>
          {KINDS.map((k) => (
            <button
              key={k}
              className={`btn quiet${kinds.includes(k) ? ' on' : ''}`}
              aria-pressed={kinds.includes(k)}
              onClick={() => toggle(k)}
            >
              {KIND_LABEL[k]}
            </button>
          ))}
          {customer.locations.length > 1 && (
            <select
              className="field loc"
              aria-label="Address"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">Every address</option>
              {customer.locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name ?? l.line1}
                </option>
              ))}
            </select>
          )}
        </div>
        {error && <p className="error">{error}</p>}
        {!history && !error && <Loading error={null} what="the history" />}
        {history && !history.events.length && <p className="muted">Nothing here yet.</p>}
        {history &&
          byDay(history.events, timeZone).map((g) => (
            <div key={g.day} className="day">
              <h3 className="dayhead">{g.day}</h3>
              <ul className="rows">
                {g.events.map((e, i) => (
                  <li key={`${e.kind}-${e.at}-${i}`} className={`stone ev ${e.kind}`}>
                    <span className="when">{timeOf(e.at, timeZone)}</span>
                    <div className="what">
                      <span className={`chip say ${KIND_CHIP[e.kind]}`}>
                        {KIND_LABEL[e.kind].replace(/s$| & .*$/, '')}
                      </span>{' '}
                      {/* in Blueprint a row's title is its one way in (DESIGN.md §3: one action a row) */}
                      {bp && opensFrom(e) ? (
                        <button
                          className="bp-open"
                          aria-label={opensFrom(e)!.label}
                          onClick={opensFrom(e)!.go}
                        >
                          {jobTitle(e)}
                        </button>
                      ) : (
                        <b>{jobTitle(e)}</b>
                      )}
                      <p className="muted small">{plainText(eventDetail(e))}</p>
                      {/* the customer's signature accepting the work, as signed (QA-727) */}
                      {e.signature && <SignaturePad s={e.signature} small />}
                      {/* what the office wrote on the call (calls item 6) */}
                      {e.kind === 'call' && e.callNotes && <p className="call-notes small">{e.callNotes}</p>}
                      {mayPlay && e.kind === 'call' && e.callId && playable(e) && (
                        <StRecording callId={e.callId} what={playable(e)!} />
                      )}
                      {mayPlay && e.kind === 'call' && e.callId && e.twilioRecording && (
                        <CallTranscript callId={e.callId} compact />
                      )}
                    </div>
                    {e.amountCents !== null && <span className="amt">{money(e.amountCents)}</span>}
                    {!bp && e.kind === 'job' && e.jobId && (
                      <button
                        className="btn quiet sm ev-act"
                        aria-label={rowActionName('Open job', e)}
                        onClick={() => openJob(e.jobId!)}
                      >
                        Open job
                      </button>
                    )}
                    {!bp && e.kind === 'invoice' && e.invoiceId && (
                      <button
                        className="btn quiet sm ev-act"
                        aria-label={rowActionName('Open invoice', e)}
                        onClick={() => openInvoice(e.invoiceId!)}
                      >
                        Open invoice
                      </button>
                    )}
                    {onEditNote && e.kind === 'note' && e.note?.editable && (
                      <button
                        className="btn quiet sm ev-act"
                        aria-label={`Edit the ${e.title.charAt(0).toLowerCase()}${e.title.slice(1)}`}
                        onClick={() => onEditNote(e)}
                      >
                        Edit
                      </button>
                    )}
                    {!bp && onMessages && e.kind === 'message' && (
                      <button
                        className="btn quiet sm ev-act"
                        aria-label={`Open in Messages: ${customer.displayName}'s messages`}
                        onClick={onMessages}
                      >
                        Open in Messages
                      </button>
                    )}
                    {/* in Blueprint they're on the job's sheet: the row's one action is its title */}
                    {!bp && onNote && e.kind === 'job' && e.jobId && (
                      <button
                        className="btn quiet sm ev-act"
                        aria-label={`Add note to ${jobTitle(e)}`}
                        onClick={() => onNote({ id: e.jobId!, title: jobTitle(e) })}
                      >
                        Add note
                      </button>
                    )}
                    {!bp && onReopen && e.kind === 'job' && e.jobId && e.status === 'completed' && (
                      <button
                        className="btn quiet sm ev-act"
                        aria-label={`Reopen ${jobTitle(e)} for a warranty callback`}
                        onClick={() => onReopen({ id: e.jobId!, title: jobTitle(e) })}
                      >
                        Reopen…
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </Part>
    </>
  );
}

// A call that came over from ServiceTitan: its recording or voicemail, kept two years
// (decision 9). The link lasts five minutes and every playback is logged (migration.playRecording).
function StRecording({ callId, what }: { callId: string; what: NonNullable<ReturnType<typeof playable>> }) {
  const [src, setSrc] = useState<{ url: string; voicemail: boolean } | null>(null);
  const [busy, setBusy] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const play = async (voicemail: boolean) => {
    setBusy(voicemail);
    setError(null);
    try {
      const r = await client.call<{ url: string }>('migration.playRecording', { callId, voicemail });
      setSrc({ url: r.url, voicemail });
    } catch (e) {
      setError(e instanceof RpcError && e.code === 'not_found' ? 'No longer kept (two years).' : message(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="st-rec">
      {what.gone.map((g) => (
        <p key={g} className="muted small">
          {g}
        </p>
      ))}
      {what.recording && (
        <button className="btn quiet sm" disabled={busy !== null} onClick={() => void play(false)}>
          {busy === false ? 'Getting it…' : 'Play the recording'}
        </button>
      )}
      {what.voicemail && (
        <button className="btn quiet sm" disabled={busy !== null} onClick={() => void play(true)}>
          {busy === true ? 'Getting it…' : 'Play the voicemail'}
        </button>
      )}
      {error && (
        <span className="error small" role="alert">
          {error}
        </span>
      )}
      {src && (
        <audio
          controls
          autoPlay
          src={src.url}
          aria-label={src.voicemail ? 'The voicemail' : 'The call recording'}
          onError={() => setError('It couldn’t be played. Try again.')}
        />
      )}
    </div>
  );
}

// ───────── what we've done at this address (office-history prototype) ─────────
// Its own query of the address's jobs, so the kind filters below don't change it; it follows the
// Address picker. Owing comes from customers.money, kept to this address's jobs; a role that can't
// see money gets no Owing.

function AddressSummaryCard({
  customer,
  locationId,
  timeZone,
}: {
  customer: Customer;
  locationId: string;
  timeZone: string;
}) {
  const Part = usePart();
  const [jobs, setJobs] = useState<History | null>(null);
  const [m, setM] = useState<Money | null | undefined>(undefined);
  useEffect(() => {
    setJobs(null);
    client
      .call<History>('customers.history', {
        customerId: customer.id,
        kinds: ['job'],
        ...(locationId ? { locationId } : {}),
      })
      .then(setJobs)
      .catch(() => undefined);
  }, [customer.id, locationId]);
  useEffect(() => {
    client
      .call<Money>('customers.money', { customerId: customer.id })
      .then(setM)
      .catch(() => setM(null));
  }, [customer.id]);
  if (!jobs || m === undefined) return null;
  const s = addressSummary(jobs, m, {
    timeZone,
    withJobless: !locationId || customer.locations.length <= 1,
  });
  const where = locationId
    ? (customer.locations.find((l) => l.id === locationId)?.line1 ?? 'this address')
    : customer.locations.length > 1
      ? `these ${customer.locations.length} addresses`
      : (customer.locations[0]?.line1 ?? 'this address');
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  return (
    <Part className={Part === 'div' ? 'addr-sum' : 'sheet addr-sum'} aria-labelledby="addr-sum-title">
      <h2 id="addr-sum-title">What we’ve done at this address</h2>
      <p className="addr-short">
        {s.visits ? (
          <>
            <b>{jobsAndVisits(s)}</b>
            {s.since && ` since ${longDate(s.since)}`}
            {s.mostly && ` · mostly ${s.mostly.toLowerCase()}`}
            {s.today && (
              <>
                {' · '}
                <b>today</b> #{s.today}
              </>
            )}
          </>
        ) : (
          'No visits yet.'
        )}
      </p>
      {(s.visits > 0 || (s.owing?.cents ?? 0) > 0) && (
        <div className="sumgrid">
          <div className="pebble">
            <span>Last visit</span>
            <b>{s.last ? (s.last.today ? 'Today' : longDate(s.last.date)) : '—'}</b>
            {s.last && <span className="pd">{s.last.what}</span>}
          </div>
          {s.equipment && (
            <div className="pebble">
              <span>Equipment</span>
              <b>
                {s.equipment.length || 'None'}
                {s.equipment.length > 0 && <small> on record</small>}
              </b>
              <span className="pd">
                {s.equipment
                  .map((e) => (e.installedYear ? `${e.kind} (${e.installedYear})` : e.kind))
                  .join(', ')}
              </span>
            </div>
          )}
          {s.usualTech && (
            <div className="pebble">
              <span>Usual tech</span>
              <b>{s.usualTech.name}</b>
              <span className="pd">{plural(s.usualTech.visits, 'visit')}</span>
            </div>
          )}
          {s.owing && (
            <div className="pebble">
              <span>Owing</span>
              <b className={s.owing.cents > 0 ? 'bad' : 'ok'}>{big(s.owing.cents)}</b>
              <span className="pd">{plural(s.owing.invoices, 'open invoice')}</span>
            </div>
          )}
        </div>
      )}
      {s.visits > 0 && (
        <details className="more">
          <summary>More</summary>
          <p>
            {summaryLine(jobs.summary, longDate).replace('We have been here', `We have been to ${where}`)}
            {s.today && ` Today: #${s.today}.`}
          </p>
        </details>
      )}
    </Part>
  );
}

interface Photo {
  id: string;
  jobId: string;
  jobNumber: string;
  category: string;
  caption: string | null;
  kind: 'photo' | 'video' | 'document' | 'signature';
  contentType: string;
  capturedAt: string | null;
  status: string;
  url: string | null;
  originalUrl: string | null;
  /** A picture's thumbnail, for the grid. */
  thumbUrl?: string | null;
  annotated: boolean;
  /** How it shows (photo-display-model.ts). */
  display?: Display;
  /** customer: on the portal; internal: the office's only. */
  visibility: string;
  /** On a job at their address filed under another customer (history fix 2). */
  otherCustomer?: boolean;
  /** Who added it, by name. */
  addedBy: string | null;
  /** A video on its way up in parts (BL-082); null otherwise. */
  upload?: VideoUpload | null;
  /** Why a video failed its check (status 'failed'). */
  failedReason?: string | null;
  /** Pending again because what arrived wasn't the file (#73): "Upload it again". */
  checkFailed?: boolean;
}

/** Every estimate shown to them, job by job, and which one they picked (customers.estimates). */
function EstimatesTab({ customerId, timeZone }: { customerId: string; timeZone: string }) {
  const Part = usePart();
  const [list, setList] = useState<CustomerEstimate[] | null>(null);
  // ServiceTitan's while the company mirrors it: said so on each (history fix 1)
  const mirroring = useMirror();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<{ estimates: CustomerEstimate[] }>('customers.estimates', { customerId })
      .then((r) => setList(r.estimates))
      .catch((e: unknown) => setError(message(e)));
  }, [customerId]);
  if (error) return <p className="error">{error}</p>;
  if (!list) return <Loading error={null} what="the estimates" />;
  if (!list.length) return <p className="muted">No estimates yet.</p>;
  const when = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' });
  const day = (at: string | null) => (at ? when.format(new Date(at)) : null);
  return (
    <Part className={Part === 'div' ? 'bp-tab-part' : 'card'} aria-label="Estimates">
      {byJob(list).map((j) => (
        <div key={j.jobId} className="day">
          <div className="row">
            <h3 className="dayhead">
              Job #{j.jobNumber} <span className="muted small">{j.jobType}</span>
            </h3>
            <button
              className="btn quiet sm"
              aria-label={`Open job ${j.jobNumber}`}
              onClick={() => openJob(j.jobId)}
            >
              Open job
            </button>
          </div>
          <ul className="est-list">
            {j.estimates.map((e) => {
              const o = outcome(e, j.picked);
              const dated =
                e.status === 'sold'
                  ? day(e.soldAt) && `on ${day(e.soldAt)}${e.soldBy ? ` by ${e.soldBy}` : ''}`
                  : day(e.presentedAt) && `shown ${day(e.presentedAt)}`;
              return (
                <li key={e.id} className={e.status === 'sold' ? 'est-picked' : undefined}>
                  <div className="row">
                    <b>{e.title}</b>
                    <span className="muted small">#{e.number}</span>
                    <span className={`chip say ${o.tone}`}>{o.words}</span>
                    {/* ServiceTitan's while mirroring; one it took off says so whatever the mode (history fix 2) */}
                    {stEstimateWords(e, mirroring).map((w) => (
                      <span key={w} className="muted small">
                        {w}
                      </span>
                    ))}
                    {dated && <span className="muted small">{dated}</span>}
                  </div>
                  <ul className="small">
                    {e.options.map((op) => (
                      <li key={op.id}>
                        {op.selected ? <b>{op.title}</b> : op.title} · {money(op.totalCents)}
                        {op.selected && e.options.length > 1 && (
                          <span className="muted"> (the one bought)</span>
                        )}
                        {/* the total with tax, as ServiceTitan shows it; before tax beneath (history fix 1) */}
                        {op.taxCents && op.subtotalCents !== undefined ? (
                          <span className="muted est-before-tax">{money(op.subtotalCents)} before tax</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </Part>
  );
}

function PhotosTab({
  customerId,
  timeZone,
  onAdd,
  canHide = false,
}: {
  customerId: string;
  timeZone: string;
  /** Add an office file to one of their jobs (media.add), for those who may. */
  onAdd?: () => void;
  /** Hide a photo from the customer, or show it again (attachments.setVisibility: jobs.complete). */
  canHide?: boolean;
}) {
  const Part = usePart();
  const [photos, setPhotos] = useState<Photo[] | null>(null);
  // more files than even ALL_FILES: the oldest are left out, and the tab says so
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [n, setN] = useState(0);
  const [filter, setFilter] = useState<GalleryFilter>(ALL);
  const [viewing, setViewing] = useState<number | null>(null);
  useEffect(() => {
    client
      // the files the office adds (a signed quote, a permit) beside the techs' photos
      // every one of them (history fix 2: the server's first 200 left an address's older photos out)
      .call<{ photos: Photo[]; more?: boolean }>('customers.photos', {
        customerId,
        kinds: ['photo', 'video', 'document'],
        limit: ALL_FILES,
      })
      .then((r) => {
        setPhotos(r.photos);
        setMore(!!r.more);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [customerId, n]);
  const setVisibility = async (p: Photo, visibility: 'customer' | 'internal') => {
    setBusy(true);
    setSaid(null);
    try {
      await client.call('attachments.setVisibility', { attachmentId: p.id, visibility });
      setSaid(visibility === 'internal' ? 'Hidden from the customer.' : 'Shown to the customer again.');
      setN((x) => x + 1);
    } catch (e) {
      setSaid(message(e));
    } finally {
      setBusy(false);
    }
  };
  const add = onAdd && (
    <div className="row">
      <button className="btn" onClick={onAdd}>
        Add a file
      </button>
    </div>
  );
  if (error) return <p className="error">{error}</p>;
  if (!photos) return <Loading error={null} what="the photos" />;
  if (!photos.length)
    return (
      <>
        {add}
        <p className="muted">No photos yet.</p>
      </>
    );
  // the gallery's choice over everything, kept in its job; what opens full size is its photos and videos
  const shown = galleryOf(photos, filter);
  const { jobs, viewable } = filesByJob(shown);
  // each one's note, else a made-up name numbered in its job, whatever is chosen (P-0161)
  const names = namesOf(photos);
  const when = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' });
  return (
    <Part className={Part === 'div' ? 'bp-tab-part' : 'card'} aria-label="Photos">
      {add}
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
      <GalleryTools items={photos} value={filter} onChange={setFilter} />
      {more && (
        <p className="muted small">The newest {photos.length.toLocaleString('en-CA')} files are shown.</p>
      )}
      {!shown.length && <p className="muted">None of these.</p>}
      {jobs.map(([jobId, number]) => (
        <div key={jobId} className="day">
          <h3 className="dayhead">
            Job #{number}
            {/* a job at their address filed under someone else: said so, never whose (history fix 2) */}
            {shown.some((p) => p.jobId === jobId && p.otherCustomer) && (
              <span className="muted small"> · for another customer</span>
            )}
          </h3>
          <ul className="photo-grid">
            {shown
              .filter((p) => p.jobId === jobId && !isOtherFile(p))
              .map((p) => {
                // its thumbnail; the viewer shows its display copy (photo-display-model.ts)
                const tile = tileOf(p);
                const caption = photoNote(p.caption);
                return (
                  <li key={p.id} className="pg-tile">
                    {tile.t === 'picture' || tile.t === 'video' ? (
                      <button
                        className="pg-open"
                        aria-label={`Open ${caption ?? `the ${p.category} ${p.kind}`}, job ${number}, full size`}
                        onClick={() => setViewing(viewable.findIndex((x) => x.id === p.id))}
                      >
                        {tile.t === 'video' ? (
                          <span className="ph small">▶ Video</span>
                        ) : (
                          <img src={tile.src} alt="" loading="lazy" />
                        )}
                      </button>
                    ) : tile.t === 'file' ? (
                      <a className="ph small" href={tile.href ?? undefined} target="_blank" rel="noreferrer">
                        {tile.words}
                      </a>
                    ) : (
                      <span className="ph muted small" title={p.failedReason ?? undefined}>
                        {tile.t === 'waiting' && p.kind !== 'video' ? tile.words : <VideoWords p={p} />}
                      </span>
                    )}
                    <span className="small muted pg-caption" title={photoFileName(p.caption) ?? undefined}>
                      {names.get(p.id)}
                      {p.capturedAt ? ` · ${when.format(new Date(p.capturedAt))}` : ''}
                      {p.addedBy ? ` · ${p.addedBy}` : ''}
                    </span>
                    <MarkedUp p={p} />
                    {p.kind === 'photo' && (
                      <PhotoVisibility
                        p={p}
                        busy={busy}
                        {...(canHide ? { onSet: (v) => void setVisibility(p, v) } : {})}
                      />
                    )}
                  </li>
                );
              })}
          </ul>
          {/* kept with the job, out of the photo strip: MMS wrappers, contact cards */}
          {shown.some((p) => p.jobId === jobId && isOtherFile(p)) && (
            <details className="small">
              <summary>Other files</summary>
              <ul>
                {shown
                  .filter((p) => p.jobId === jobId && isOtherFile(p))
                  .map((p) => (
                    <li key={p.id}>
                      {p.url ? (
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          title={photoFileName(p.caption) ?? undefined}
                        >
                          {names.get(p.id) ?? 'A file'}
                        </a>
                      ) : (
                        (names.get(p.id) ?? 'A file')
                      )}
                      <span className="muted"> · {p.contentType}</span>
                    </li>
                  ))}
              </ul>
            </details>
          )}
        </div>
      ))}
      {viewing !== null && viewing >= 0 && (
        <PhotoViewer
          photos={viewable}
          start={viewing}
          timeZone={timeZone}
          onClose={() => setViewing(null)}
          {...(canHide
            ? {
                onCaption: async (attachmentId: string, caption: string | null) => {
                  await client.call('attachments.setCaption', { attachmentId, caption });
                  setN((x) => x + 1);
                },
              }
            : {})}
        />
      )}
    </Part>
  );
}

function MoneyTab({
  customerId,
  customerName,
  focus,
  held,
  timeZone,
  onMoved,
  onGone,
  reopen,
  onPaying,
}: {
  customerId: string;
  customerName: string;
  focus?: string;
  held: readonly string[];
  timeZone: string;
  onMoved: () => void;
  /** A read answered not found: true when the customer was merged meanwhile and the kept one opens. */
  onGone: () => Promise<boolean>;
  /** The payment being typed when the merged customer's tab was open: it opens again, as typed. */
  reopen?: Paying;
  /** The payment being typed, or null when none is open. */
  onPaying: (p: Paying | null) => void;
}) {
  const Part = usePart();
  // Blueprint: a payment's receipt Send goes under its row's More, with Dispute… and Void…
  const bp = Part === 'div';
  const [m, setM] = useState<Money | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Refunds, credits, write-offs and disputes (gap #8): each one done reloads the tab and says so.
  // While the company mirrors ServiceTitan, its money is ServiceTitan's: the tab reads it (BL-088).
  const mirroring = useMirror();
  const can = moneyRights(held, mirroring);
  const [moved, setMoved] = useState(0);
  const [said, setSaid] = useState<string | null>(null);
  // Record a payment (books' shared form, F-07): opened from an invoice's row, that invoice first.
  const [paying, setPaying] = useState<string | null>(reopen?.invoiceId ?? null);
  // what was typed before the merge, for the form it reopens once (QA-559)
  const [carried, setCarried] = useState(reopen);
  // the invoices and payments shown so far, a page at a time (history fix 4: one customer has 2,499)
  const [shownInvoices, setShownInvoices] = useState(MONEY_PAGE);
  const [shownPayments, setShownPayments] = useState(MONEY_PAGE);
  // leaving the tab leaves the payment behind
  useEffect(() => () => onPaying(null), []);
  const done = (words: string) => {
    setSaid(words);
    setMoved((n) => n + 1);
    onMoved();
  };
  useEffect(() => {
    client
      .call<Money>('customers.money', { customerId })
      .then(setM)
      .catch(async (e: unknown) => {
        if (customerGone(e) && (await onGone())) return;
        setError(message(e));
      });
  }, [customerId, moved]);
  // a payment or write-off made in the invoice dialog over this tab (QA-805)
  useEffect(() => onInvoiceMoved(window, customerId, () => setMoved((n) => n + 1)), [customerId]);
  useDocFocus(focus, !!m);
  if (error) return <p className="error">{error}</p>;
  if (!m) return <Loading error={null} what="what they owe" />;
  const fmt = (d: string | null) => (d ? longDate(d) : '—');
  const invoices = moneyPage(m.invoices, shownInvoices, focus);
  const listed = m.payments ? listedPayments(m.payments) : null;
  const payments = listed ? moneyPage(listed.payments, shownPayments, focus) : null;
  return (
    <Part className={Part === 'div' ? 'bp-tab-part' : 'card'} aria-label="Money">
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {mirroring && moneyRights(held).record && <p className="muted small">{MIRROR_LINE}</p>}
      <div className="sumgrid">
        <div className="stone pebble">
          <span className="muted small">Open balance</span>
          <b className={m.openCents ? 'bad' : 'ok'}>{money(m.openCents)}</b>
        </div>
        <div className="stone pebble">
          <span className="muted small">Overdue</span>
          <b>{money(m.overdueCents)}</b>
        </div>
        <div className="stone pebble">
          <span className="muted small">Paid to date</span>
          <b>{money(m.paidCents)}</b>
        </div>
        {m.creditCents !== null && (
          <div className="stone pebble">
            <span className="muted small">Credit on account</span>
            <b>{money(m.creditCents)}</b>
          </div>
        )}
      </div>
      <h3>Invoices</h3>
      {m.invoices.length ? (
        <div className="tablewrap" ref={scrollFocus}>
          <table className="t money-invoices">
            <thead>
              <tr>
                <th>Invoice</th>
                {/* on a phone, said under the invoice's number instead, so the table fits */}
                <th className="wide-col">Issued</th>
                <th className="wide-col">Job</th>
                <th className="num">Total</th>
                <th className="num">Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            {invoices.rows.map((i) => (
              <tbody key={i.id} className="inv">
                <tr className="inv-row" data-doc={i.id}>
                  <td>
                    <button className="inv-link" onClick={() => openInvoice(i.id)}>
                      {i.number}
                    </button>
                    <span className="phone-only muted small">
                      {fmt(i.issuedOn)}
                      {i.jobNumber ? ` · #${i.jobNumber}` : ''}
                    </span>
                  </td>
                  <td className="wide-col">{fmt(i.issuedOn)}</td>
                  <td className="wide-col">{i.jobNumber ? `#${i.jobNumber}` : '—'}</td>
                  <td className="num">{money(i.totalCents)}</td>
                  <td className="num">{money(i.balanceCents)}</td>
                  <td>
                    <span
                      className={`chip say ${i.overdue ? 'bad' : i.balanceCents > 0 || i.status === 'written_off' ? '' : 'ok'}`}
                    >
                      {i.overdue ? 'Overdue' : invoiceStatusWord(i.status)}
                    </span>
                  </td>
                </tr>
                {/* its actions on a row of their own: on a phone the table scrolls sideways, and they stay in view */}
                <tr className="inv-acts-row">
                  <td colSpan={6} className="inv-acts">
                    <InvoiceMoneyActions
                      invoice={i}
                      rights={can}
                      version={moved}
                      timeZone={timeZone}
                      onDone={done}
                      onRecord={() => setPaying(i.id)}
                    />
                  </td>
                </tr>
              </tbody>
            ))}
          </table>
          {invoices.more > 0 && (
            <button className="btn quiet" onClick={() => setShownInvoices((n) => n + MONEY_PAGE)}>
              {showMoreText(invoices.more)}
            </button>
          )}
        </div>
      ) : (
        <p className="muted">No invoices yet.</p>
      )}
      {paying && (
        // the form's own look is the books'
        <div className="books money-pay">
          <RecordPayment
            key={paying}
            customerId={customerId}
            customerName={customerName}
            firstInvoiceId={paying}
            timeZone={timeZone}
            dates={held.includes('invoices.edit')}
            {...(carried?.invoiceId === paying ? { draft: carried.draft } : {})}
            onDraft={(draft) => onPaying({ invoiceId: paying, draft })}
            toast={setSaid}
            onDone={(recorded) => {
              setPaying(null);
              setCarried(undefined);
              onPaying(null);
              if (recorded) {
                setMoved((n) => n + 1);
                onMoved();
              }
            }}
          />
        </div>
      )}
      {listed && payments && (
        <>
          <h3>Payments</h3>
          {payments.rows.length ? (
            <ul className="rows">
              {payments.rows.map((p) => (
                <li key={p.id} className="mat" data-doc={p.id}>
                  <span className="pay-what">
                    {new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' }).format(
                      new Date(p.receivedAt),
                    )}{' '}
                    · {methodWord(p.method)}
                    <PaymentStatus payment={p} />
                    <PaymentReceiptLine
                      payment={p}
                      channel={m.receiptChannel}
                      canSend={can.record}
                      timeZone={timeZone}
                      onDone={done}
                      {...(bp ? { inline: false } : {})}
                    />
                  </span>
                  <span className="row money-acts">
                    {p.status === 'void' ? (
                      // kept for the record, so it can't be read as money received
                      <s className="muted">
                        <span className="sr-only">Voided, </span>
                        {money(p.amountCents)}
                      </s>
                    ) : (
                      <b>{money(p.amountCents)}</b>
                    )}
                    <PaymentActions
                      payment={p}
                      rights={can}
                      onDone={done}
                      {...(bp && can.record && canResend(p, m.receiptChannel)
                        ? {
                            more: (
                              <ReceiptSend
                                payment={p}
                                channel={m.receiptChannel}
                                canSend={can.record}
                                onDone={done}
                              />
                            ),
                          }
                        : {})}
                    />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            listed.zero === 0 && <p className="muted">No payments yet.</p>
          )}
          {payments.more > 0 && (
            <button className="btn quiet" onClick={() => setShownPayments((n) => n + MONEY_PAGE)}>
              {showMoreText(payments.more)}
            </button>
          )}
          {listed.zero > 0 && (
            <p className="muted small">
              {listed.zero === 1
                ? 'A $0.00 payment brought from ServiceTitan isn’t listed.'
                : `${listed.zero} payments of $0.00 brought from ServiceTitan aren’t listed.`}
            </p>
          )}
        </>
      )}
      {m.payments !== null && (can.refund || can.edit) && (
        <Credits customerId={customerId} invoices={m.invoices} rights={can} version={moved} onDone={done} />
      )}
      {m.payments === null && (
        <p className="muted small">
          Payment methods and credits are shown to the owner, managers and the bookkeeper.
        </p>
      )}
    </Part>
  );
}

function AuditTab({ customerId, timeZone }: { customerId: string; timeZone: string }) {
  const Part = usePart();
  const [rows, setRows] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<{ entries: AuditEntry[] }>('customers.audit', { customerId })
      .then((r) => setRows(r.entries))
      .catch((e: unknown) => setError(message(e)));
  }, [customerId]);
  if (error) return <p className="error">{error}</p>;
  if (!rows) return <Loading error={null} what="the audit trail" kind="table" />;
  const when = new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' });
  return (
    <Part className={Part === 'div' ? 'bp-tab-part' : 'card'} aria-label="Audit">
      <p className="muted small">
        {rows.length} changes · append-only. Values aren’t shown here; fields changed are.
      </p>
      <ul className="rows">
        {rows.map((a) => (
          // Who, what, then when, as the app's other rows read: "Brook Owner · Booked a job · on the invoice · …"
          <li key={a.id} className="stone au">
            <p className="au-line">
              <b>{a.who}</b>
              {[
                pastTense(a.title),
                `on the ${a.entityType}`,
                SOURCE_WORDS[a.source] ?? null,
                when.format(new Date(a.at)),
              ]
                .filter(Boolean)
                .map((part) => (
                  <span key={part}> · {part}</span>
                ))}
            </p>
            {a.fields.length > 0 && (
              <p className="muted small">Changed: {a.fields.map(fieldWords).join(', ')}</p>
            )}
          </li>
        ))}
      </ul>
    </Part>
  );
}

function StatementDialog({
  customer,
  timeZone,
  onClose,
}: {
  customer: Customer;
  timeZone: string;
  onClose: () => void;
}) {
  const period = statementPeriod(localDate(timeZone));
  const [s, setS] = useState<{
    openingCents: number;
    closingCents: number;
    lines: { date: string; what: string; chargeCents: number; creditCents: number; balanceCents: number }[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  useEffect(() => {
    client
      .call<NonNullable<typeof s>>('receivables.statement', { customerId: customer.id, ...period })
      .then(setS)
      .catch((e: unknown) => setError(message(e)));
  }, [customer.id]);
  const download = async () => {
    try {
      const pdf = await client.call<{ filename: string; base64: string }>('receivables.statementPdf', {
        customerId: customer.id,
        ...period,
      });
      const a = document.createElement('a');
      a.href = `data:application/pdf;base64,${pdf.base64}`;
      a.download = pdf.filename;
      a.click();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <dialog ref={ref} className="sheet ask wide" aria-labelledby="stmt-title" onClose={onClose}>
      <h3 id="stmt-title">Statement · {customer.displayName}</h3>
      <p className="muted small">
        {longDate(period.from)} to {longDate(period.to)}
      </p>
      {error && <p className="error">{error}</p>}
      {s && (
        <table className="t">
          <tbody>
            <tr>
              <td>Opening balance</td>
              <td className="num">{money(s.openingCents)}</td>
            </tr>
            {s.lines.map((l, i) => (
              <tr key={i}>
                <td>
                  {longDate(l.date)} · {l.what}
                </td>
                <td className="num">{money(l.chargeCents - l.creditCents)}</td>
              </tr>
            ))}
            <tr className="total">
              <td>Closing balance</td>
              <td className="num">{money(s.closingCents)}</td>
            </tr>
          </tbody>
        </table>
      )}
      <div className="row end">
        <button className="btn soft" onClick={() => ref.current?.close()}>
          Close
        </button>
        <button className="btn primary" onClick={() => void download()}>
          Download PDF
        </button>
      </div>
    </dialog>
  );
}
